import Button from 'components/Button';
import FormActionGroup from 'components/FormActionGroup';
import {GooglePlaceAutoComplete} from 'components/GooglePlaceAutoComplete';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import {Controller, get, SubmitHandler, useForm} from 'react-hook-form';
import toast, {Toaster} from 'react-hot-toast';
import styles from './StripeForm.module.scss';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {useState} from 'react';
import api from 'api';
import {CreditCardIcon} from '../../icons';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import FormGroup from 'components/FormGroup';

type StripeFormProps = {
  paymentId: string;
  onSuccess: () => void;
  copayItems: {id: number; amount_due: string}[];
};

type FormData = {
  cardholder_name: string;
  billing_address: {
    address_line_1: string;
    city: string;
    state: string;
    zipcode: string;
    latitude: number;
    longitude: number;
  };
};

function StripeForm({paymentId, onSuccess, copayItems}: StripeFormProps) {
  const [loading, setLoading] = useState(false);
  const {
    control,
    setValue,
    handleSubmit,
    formState: {errors},
  } = useForm<FormData>();
  const stripe = useStripe();
  const elements = useElements();
  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;
    try {
      setLoading(true);
      const response = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (response.error) {
        toast.error(response.error.message || 'Something went wrong');
        return;
      }
      const body = {
        id: paymentId,
        card_type: get(response, 'paymentMethod.card.display_brand', ''),
        card_number:
          '************' + get(response, 'paymentMethod.card.last4', ''),
        cardholder_name: data.cardholder_name,
        token: response?.paymentMethod.id,
        copay_items: copayItems,
        billing_address: data.billing_address,
      };
      const {data: paymentResponse} = await api.post('/payment/links/', body);
      toast.success(paymentResponse.message || 'Payment successful');
      onSuccess();
    } catch (error) {
      toast.error(get(error, 'message', 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card className={styles.card}>
      <CardContent pt="sm" className={styles.cardContent}>
        <Typography className={styles.title} variant="h4">
          <CreditCardIcon className={styles.cardIcon} />
          Payment Method
        </Typography>
        <Typography variant="label">Card Number</Typography>
        <FormGroup className={styles.cardNumberGroup}>
          <CardNumberElement />
        </FormGroup>
        <div className={styles.cardExpiryGroup}>
          <div>
            <Typography variant="label">Expiration Date</Typography>
            <CardExpiryElement className={styles.cardExpiryElement} />
          </div>
          <div>
            <Typography variant="label">CVC</Typography>
            <CardCvcElement className={styles.cardCvcElement} />
          </div>
        </div>
        <Controller
          name="cardholder_name"
          control={control}
          rules={{required: 'Please, fill holder name'}}
          render={({field, fieldState}) => (
            <TextField
              value={field.value}
              label="Card holder name"
              placeholder="Enter card holder name"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <GooglePlaceAutoComplete
          label="Billing address"
          placeholder="Address"
          onPlaceSelected={(data) => {
            setValue('billing_address', {
              address_line_1: data.addressLine1,
              city: data.city,
              state: data.state,
              zipcode: data.zipcode,
              latitude: data.lat,
              longitude: data.lon,
            });
          }}
          error={errors?.billing_address?.address_line_1?.message}
          options={{
            types: ['address'],
          }}
        />
        <FormActionGroup className={styles.actionWrapper}>
          <Button
            loading={loading}
            type="submit"
            className={styles.button}
            onClick={handleSubmit(onSubmit)}
          >
            Pay Now
          </Button>
        </FormActionGroup>
        <Toaster />
      </CardContent>
    </Card>
  );
}
export default StripeForm;
